// V2-only Address Change section, rendered inside the "What changed?" drawer.
//
// Redesigned per the V2 mockup:
//   - "Change" checkbox + explanatory copy about Section 4 address / re-rate.
//   - Pill selector for what's changing: Mailing only / Garaging only /
//     Mailing and garaging.
//   - "Change address on customer screen?" Yes/No (reuses the existing
//     sibling field `change_mailing_address_on_customer`).
//   - Address fields (address, unit, city, province, postal code, effective
//     date, phone, email).
//   - Active-vehicle list (from the policy) with a checkbox per vehicle and
//     CURRENT vs NEW rows for parking, commute, annual km, use, principal
//     driver and occasional driver. Only vehicles the user checks get a NEW
//     row saved to `address_change.vehicle_updates`.
//
// Kept separate from the shared V1 AddressChangeInformation step so V1 (and
// other policy types reusing that step) is unaffected.

import React from "react";
import {
  Box,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import produce from "immer";
import {
  ControlledCustomSelect,
  CustomFormLabel,
  CustomTextField,
} from "../../../../../../../../components/formsComponents";
import {
  CustomCheckbox,
} from "../../../../../../../../components/formsComponents/CustomCheckbox";
import { AddressSearchAutoComplete } from "../../../../../../../../components/AddressAutoComplete/AddressSearchAutoComplete";
import { ILocationResponsePayload } from "../../../../../../../../components/LocationAutoComplete/LocationAutoComplete";
import { provincesAndStates } from "../../../../../../../../constants/constants";
import { IAutomobilePolicyChangeProps } from "../../Steps/AutomobilePolicyChange.types";
import { useAutomobilePolicyVehcileInfo } from "../../../hooks/useAutomobileVehicleInfo";
import { useAutomobilePolicyDriverInfo } from "../../../hooks/useAutomobilePolicyDriverInfo";
import { IAutomobileAddressChangeVehicleUpdate } from "../../../../../../../../redux/automobilePolicyChangeReq/automobilePolicyChangeReq.types";

const ADDRESS_CHANGE_TYPE_OPTIONS = [
  "Mailing only",
  "Garaging only",
  "Mailing and garaging",
] as const;

// Starter options for the per-vehicle re-rate pickers, rendered with
// ControlledCustomSelect (the shared dropdown used everywhere else in this
// panel, e.g. Province and Principal/Occasional Driver).
const PARKING_OPTIONS = [
  "Private garage",
  "Driveway",
  "Carport",
  "Underground / parkade",
  "Street",
].map((label) => ({ label, value: label }));
const COMMUTE_OPTIONS = ["0", "5", "10", "15", "20", "25", "30", "40", "50"].map(
  (label) => ({ label, value: label })
);
const ANNUAL_KM_OPTIONS = [
  "8000",
  "10000",
  "12000",
  "16000",
  "20000",
  "24000",
  "30000",
].map((label) => ({ label, value: label }));
// Dedicated list for the per-vehicle "Use" picker in this panel (kept
// separate from the shared `vehicle_use` constant used elsewhere, since this
// panel's list intentionally differs — includes "Business" and "Farm").
const VEHICLE_USE_OPTIONS = ["Pleasure", "Business", "Farm"].map((label) => ({
  label,
  value: label,
}));

// This panel renders inside a RightPanel with zIndex 1400 (see
// ChangeSectionDrawer). MUI's Select opens its menu in a Popover portalled at
// the document root with the default modal z-index (1300), so without this
// override every dropdown menu in this panel renders behind the drawer and
// its options appear "missing" (the menu is open, just hidden underneath).
const SELECT_MENU_PROPS = { sx: { zIndex: 1500 } };

// Read-only "CURRENT" value shown above each editable "NEW" field.
const CurrentValue: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <Box>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ display: "block", textTransform: "uppercase", fontSize: "0.68rem" }}
    >
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={600}>
      {value || "--"}
    </Typography>
  </Box>
);

export const AddressChangeInformationV2: React.FC<
  IAutomobilePolicyChangeProps
> = ({ values, errors, setValues, handleChange, customerPolicyId }) => {
  const { vehiclesList } = useAutomobilePolicyVehcileInfo(customerPolicyId);
  const { driverList } = useAutomobilePolicyDriverInfo(customerPolicyId);

  // Active vehicles only, per requirement — the hook already requests the
  // "ACTIVE" status server-side, but we defensively filter here too in case
  // the store still holds a stale/mixed list.
  const activeVehicles = React.useMemo(
    () => vehiclesList.filter((vehicle) => vehicle.status === "ACTIVE"),
    [vehiclesList]
  );

  // Principal/occasional driver pickers must only offer active drivers. The
  // shared hook/dropdown (used elsewhere e.g. for driver delete) doesn't
  // filter by status, so it's filtered here rather than in the shared hook.
  const activeDriverDropdown = React.useMemo(
    () =>
      driverList
        .filter((driver) => driver.status === "ACTIVE")
        .map((driver) => ({
          label: `${driver.driver_number || "--"} (Driver Name: ${
            driver.driver_name || "N/A"
          } )`,
          value: driver.driver_number || "",
        })),
    [driverList]
  );

  const addressChange = values.address_change;

  // `vehicle_updates` was added after some existing change-request records
  // were created, so records loaded from the API may not have it at all.
  // Guard every read with a fallback instead of trusting the compile-time
  // type, since that type doesn't guarantee what the API actually returned.
  const vehicleUpdates = addressChange.vehicle_updates || [];

  const handleAddress = (data: ILocationResponsePayload) => {
    setValues({
      ...values,
      address_change: {
        ...addressChange,
        address: data.address,
        city: data.city,
        country: data.country,
        state: data.state,
        postal_code: data.postalCode,
      },
    });
  };

  const handleChangeTypeSelect = (
    _e: React.MouseEvent<HTMLElement>,
    value: (typeof ADDRESS_CHANGE_TYPE_OPTIONS)[number] | null
  ) => {
    if (!value) {
      return;
    }
    setValues(
      produce(values, (draft) => {
        draft.address_change.change_type = value;
      })
    );
  };

  const handleChangeOnCustomerScreen = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setValues(
      produce(values, (draft) => {
        draft.change_mailing_address_on_customer = e.target.value;
      })
    );
  };

  // Find (or lazily create) this vehicle's row in vehicle_updates.
  const getVehicleUpdate = (
    vehicleNumber: number | null
  ): IAutomobileAddressChangeVehicleUpdate => {
    const existing = vehicleUpdates.find(
      (item) => item.vehicle_number === vehicleNumber
    );
    if (existing) {
      return existing;
    }
    return {
      vehicle_number: vehicleNumber,
      selected: false,
      parking: null,
      commute: null,
      annual_km: null,
      use: null,
      principal_driver: null,
      occasional_driver: null,
    };
  };

  const updateVehicleRow = (
    vehicleNumber: number | null,
    patch: Partial<IAutomobileAddressChangeVehicleUpdate>
  ) => {
    setValues(
      produce(values, (draft) => {
        if (!draft.address_change.vehicle_updates) {
          draft.address_change.vehicle_updates = [];
        }
        const list = draft.address_change.vehicle_updates;
        const index = list.findIndex(
          (item) => item.vehicle_number === vehicleNumber
        );
        if (index === -1) {
          list.push({
            ...getVehicleUpdate(vehicleNumber),
            ...patch,
          });
        } else {
          list[index] = { ...list[index], ...patch };
        }
      })
    );
  };

  const handleVehicleToggle = (vehicleNumber: number | null) => (
    _e: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    updateVehicleRow(vehicleNumber, { selected: checked });
  };

  return (
    <>
      {/* "Change" toggle for the whole section, matching the mockup's header checkbox. */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <CustomCheckbox
          checked={!!addressChange.address}
          disabled
          sx={{ p: 0 }}
        />
        <Typography variant="subtitle1" fontWeight={700}>
          Change
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Section 4 address. Garaging re-rates each selected unit by territory,
        and a move usually changes commute, annual distance and parking too.
      </Typography>

      {/* Mailing only / Garaging only / Mailing and garaging */}
      <Box sx={{ mb: 2 }}>
        <ToggleButtonGroup
          value={addressChange.change_type}
          exclusive
          size="small"
          onChange={handleChangeTypeSelect}
          sx={{
            borderRadius: "50px",
            border: "1px solid",
            borderColor: "divider",
            gap: 0.25,
            p: 0.4,
            "& .MuiToggleButtonGroup-grouped": {
              border: 0,
              "&:not(:first-of-type)": { borderRadius: "50px" },
              "&:first-of-type": { borderRadius: "50px" },
            },
          }}
        >
          {ADDRESS_CHANGE_TYPE_OPTIONS.map((option) => (
            <ToggleButton
              key={option}
              value={option}
              sx={{
                px: 2,
                py: 0.6,
                fontSize: "0.85rem",
                fontWeight: 600,
                textTransform: "none",
                whiteSpace: "nowrap",
                color: "text.secondary",
                "&.Mui-selected": {
                  color: "#fff",
                  backgroundColor: "primary.main",
                  "&:hover": { backgroundColor: "primary.dark" },
                },
              }}
            >
              {option}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {/* Change address on customer screen? */}
      <Box sx={{ mb: 2 }}>
        <CustomFormLabel sx={{ mt: 0 }}>
          Change address on customer screen? *
        </CustomFormLabel>
        <FormControl component="fieldset">
          <RadioGroup
            row
            name="change_mailing_address_on_customer"
            value={values.change_mailing_address_on_customer || ""}
            onChange={handleChangeOnCustomerScreen}
          >
            <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
            <FormControlLabel value="No" control={<Radio />} label="No" />
          </RadioGroup>
        </FormControl>
      </Box>

      {/* Address row 1: address search, unit, city, province */}
      <Grid container spacing={1} sx={{ mb: 1 }}>
        <Grid item xs={12} md={5}>
          <AddressSearchAutoComplete
            address={addressChange.address}
            onAddressChange={handleAddress}
            // This panel renders in a RightPanel with zIndex 1400 (see
            // ChangeSectionDrawer); without this the "Search Address"
            // dialog opens at MUI's default modal z-index (1300) and ends
            // up stacked behind the drawer instead of on top of it.
            dialogZIndex={1500}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <CustomFormLabel>Unit</CustomFormLabel>
          <CustomTextField
            name="address_change.unit_number"
            variant="outlined"
            size="small"
            fullWidth
            value={addressChange.unit_number || ""}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <CustomFormLabel>City</CustomFormLabel>
          <CustomTextField
            name="address_change.city"
            variant="outlined"
            size="small"
            fullWidth
            value={addressChange.city || ""}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <CustomFormLabel>Province</CustomFormLabel>
          <ControlledCustomSelect
            variant="outlined"
            size="small"
            type="string"
            fullWidth
            displayEmpty
            name="address_change.state"
            value={addressChange.state}
            onChange={handleChange}
            placeholder="Select"
            options={provincesAndStates}
            MenuProps={SELECT_MENU_PROPS}
          />
        </Grid>
      </Grid>

      {/* Address row 2: postal code, phone, email */}
      <Grid container spacing={1} sx={{ mb: 2 }}>
        <Grid item xs={12} md={3}>
          <CustomFormLabel>Postal Code</CustomFormLabel>
          <CustomTextField
            name="address_change.postal_code"
            variant="outlined"
            size="small"
            fullWidth
            value={addressChange.postal_code || ""}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12} md={4.5}>
          <CustomFormLabel>Phone</CustomFormLabel>
          <CustomTextField
            name="address_change.phone"
            type="tel"
            variant="outlined"
            size="small"
            fullWidth
            value={addressChange.phone || ""}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12} md={4.5}>
          <CustomFormLabel>Email</CustomFormLabel>
          <CustomTextField
            name="address_change.email"
            type="email"
            variant="outlined"
            size="small"
            fullWidth
            value={addressChange.email || ""}
            error={(errors.address_change as any)?.email ? true : false}
            helperText={(errors.address_change as any)?.email}
            onChange={handleChange}
          />
        </Grid>
      </Grid>

      {/* Active vehicles on the policy — re-rate fields per selected unit. */}
      <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 1, mb: 1 }}>
        Vehicles
      </Typography>
      <Stack spacing={1.5}>
        {activeVehicles.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No active vehicles found on this policy.
          </Typography>
        )}
        {activeVehicles.map((vehicle) => {
          const update = getVehicleUpdate(vehicle.incremental_number);
          const vehicleLabel = `${vehicle.incremental_number ?? "--"} - ${
            vehicle.year || ""
          } ${vehicle.make || ""} ${vehicle.model || ""}`.trim();

          return (
            <Box
              key={vehicle.incremental_number}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                p: 1.5,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <CustomCheckbox
                  checked={update.selected}
                  onChange={handleVehicleToggle(vehicle.incremental_number)}
                  sx={{ p: 0 }}
                />
                <Typography variant="body2" fontWeight={700}>
                  {vehicleLabel}
                </Typography>
                {vehicle.vin && (
                  <Typography variant="caption" color="text.secondary">
                    {vehicle.vin}
                  </Typography>
                )}
              </Stack>

              <Grid container spacing={1.5} sx={{ mb: 1 }}>
                <Grid item xs={6} md={2}>
                  <CurrentValue label="Parking" value="Driveway" />
                </Grid>
                <Grid item xs={6} md={2}>
                  <CurrentValue label="Commute" value={vehicle.commute} />
                </Grid>
                <Grid item xs={6} md={2}>
                  <CurrentValue label="Annual km" value={vehicle.annual_kms} />
                </Grid>
                <Grid item xs={6} md={2}>
                  <CurrentValue label="Use" value={vehicle.vehicle_use} />
                </Grid>
                <Grid item xs={6} md={2}>
                  <CurrentValue
                    label="Principal Driver"
                    value={vehicle.driver_assignment_primary}
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <CurrentValue
                    label="Occasional Driver"
                    value={vehicle.driver_assignment_occassional}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={1.5}>
                <Grid item xs={6} md={2}>
                  <ControlledCustomSelect
                    variant="outlined"
                    size="small"
                    type="string"
                    fullWidth
                    displayEmpty
                    placeholder="Parking"
                    value={update.parking || ""}
                    onChange={(e: any) =>
                      updateVehicleRow(vehicle.incremental_number, {
                        parking: e.target.value,
                      })
                    }
                    options={PARKING_OPTIONS}
                    MenuProps={SELECT_MENU_PROPS}
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <ControlledCustomSelect
                    variant="outlined"
                    size="small"
                    type="string"
                    fullWidth
                    displayEmpty
                    placeholder="km"
                    value={update.commute || ""}
                    onChange={(e: any) =>
                      updateVehicleRow(vehicle.incremental_number, {
                        commute: e.target.value,
                      })
                    }
                    options={COMMUTE_OPTIONS}
                    MenuProps={SELECT_MENU_PROPS}
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <ControlledCustomSelect
                    variant="outlined"
                    size="small"
                    type="string"
                    fullWidth
                    displayEmpty
                    placeholder="km"
                    value={update.annual_km || ""}
                    onChange={(e: any) =>
                      updateVehicleRow(vehicle.incremental_number, {
                        annual_km: e.target.value,
                      })
                    }
                    options={ANNUAL_KM_OPTIONS}
                    MenuProps={SELECT_MENU_PROPS}
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <ControlledCustomSelect
                    variant="outlined"
                    size="small"
                    type="string"
                    fullWidth
                    displayEmpty
                    placeholder="No change"
                    value={update.use || ""}
                    onChange={(e: any) =>
                      updateVehicleRow(vehicle.incremental_number, {
                        use: e.target.value,
                      })
                    }
                    options={VEHICLE_USE_OPTIONS}
                    MenuProps={SELECT_MENU_PROPS}
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <ControlledCustomSelect
                    variant="outlined"
                    size="small"
                    type="string"
                    fullWidth
                    displayEmpty
                    placeholder="Keep current"
                    value={update.principal_driver || ""}
                    onChange={(e: any) =>
                      updateVehicleRow(vehicle.incremental_number, {
                        principal_driver: e.target.value,
                      })
                    }
                    options={activeDriverDropdown}
                    MenuProps={SELECT_MENU_PROPS}
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <ControlledCustomSelect
                    variant="outlined"
                    size="small"
                    type="string"
                    fullWidth
                    displayEmpty
                    placeholder="Keep current"
                    value={update.occasional_driver || ""}
                    onChange={(e: any) =>
                      updateVehicleRow(vehicle.incremental_number, {
                        occasional_driver: e.target.value,
                      })
                    }
                    options={activeDriverDropdown}
                    MenuProps={SELECT_MENU_PROPS}
                  />
                </Grid>
              </Grid>
            </Box>
          );
        })}
      </Stack>
    </>
  );
};
